/**
 * Utilities for cleaning and formatting AI-generated content.
 */

/**
 * Patterns that match common AI conversational prefaces.
 * These appear at the start of LLM responses and are not actual content.
 */
const AI_PREFACE_PATTERNS = [
  // Spanish conversational prefaces
  /^(?:¡?Claro[!,]?\s*(?:que sí[!,]?\s*)?(?:aquí tienes|te presento|te comparto|te dejo).*?[.!]\s*)/i,
  /^(?:Aquí (?:tienes|está|te (?:presento|comparto|dejo)).*?[.!]\s*)/i,
  /^(?:He (?:creado|preparado|escrito|generado|redactado|elaborado).*?[.!]\s*)/i,
  /^(?:Te (?:presento|comparto|dejo|envío|muestro).*?[.!]\s*)/i,
  /^(?:A continuación (?:te presento|encontrarás|tienes).*?[.!]\s*)/i,
  /^(?:Con (?:mucho )?gusto[,.]?\s*(?:aquí tienes|te presento).*?[.!]\s*)/i,
  /^(?:Por supuesto[,.]?\s*(?:aquí tienes|te presento).*?[.!]\s*)/i,
  /^(?:Perfecto[,.]?\s*(?:aquí tienes|te presento).*?[.!]\s*)/i,
  /^(?:Listo[,.]?\s*(?:aquí tienes|te presento).*?[.!]\s*)/i,
  // English conversational prefaces
  /^(?:Sure[!,]?\s*(?:here(?:'s| is| you go)).*?[.!]\s*)/i,
  /^(?:Here(?:'s| is| you go).*?[.!]\s*)/i,
  /^(?:I(?:'ve| have) (?:created|prepared|written|generated|drafted).*?[.!]\s*)/i,
  /^(?:Certainly[!,]?\s*(?:here(?:'s| is)).*?[.!]\s*)/i,
  /^(?:Of course[!,]?\s*(?:here(?:'s| is)).*?[.!]\s*)/i,
]

/**
 * Multi-line preface patterns that span multiple sentences.
 * These handle cases where the AI preface extends across two sentences.
 */
const AI_MULTILINE_PREFACE_PATTERNS = [
  // "Claro, aqui tienes... Está expandido a la longitud objetivo..."
  /^(?:¡?Claro[!,]?\s*.*?[.!]\s*(?:Está|He|Se|El|La|Los|Las).*?[.!]\s*)/i,
  /^(?:Aquí (?:tienes|está).*?[.!]\s*(?:Está|He|Se|El|La|Los|Las).*?[.!]\s*)/i,
]

/**
 * Removes AI conversational prefaces from generated content.
 * Handles both single-line and multi-line prefaces.
 */
export function cleanAIContent(text: string): string {
  if (!text) return text

  let cleaned = text.trim()

  // Try multi-line patterns first (they're more specific)
  for (const pattern of AI_MULTILINE_PREFACE_PATTERNS) {
    const match = cleaned.match(pattern)
    if (match) {
      cleaned = cleaned.slice(match[0].length).trim()
      break
    }
  }

  // Try single-line patterns
  for (const pattern of AI_PREFACE_PATTERNS) {
    const match = cleaned.match(pattern)
    if (match) {
      cleaned = cleaned.slice(match[0].length).trim()
      break
    }
  }

  return cleaned
}

/**
 * Checks if the excerpt is a duplicate (or near-duplicate) of the beginning of the body.
 * Returns true if the excerpt should be hidden.
 */
export function isExcerptDuplicate(excerpt: string | null, body: string): boolean {
  if (!excerpt) return false

  const cleanExcerpt = cleanAIContent(excerpt).trim().toLowerCase()
  const cleanBody = cleanAIContent(body).trim().toLowerCase()

  // If excerpt is contained within the first 500 chars of the body
  if (cleanBody.startsWith(cleanExcerpt)) return true

  // Check for high overlap (first 200 chars)
  const excerptStart = cleanExcerpt.slice(0, 200)
  const bodyStart = cleanBody.slice(0, 200)

  // Simple similarity: if 80% of words match
  const excerptWords = new Set(excerptStart.split(/\s+/))
  const bodyWords = new Set(bodyStart.split(/\s+/))
  const intersection = [...excerptWords].filter((w) => bodyWords.has(w))
  const similarity = intersection.length / Math.max(excerptWords.size, 1)

  return similarity > 0.8
}

/**
 * Strips markdown formatting for plain text copy.
 */
export function markdownToPlainText(text: string): string {
  return text
    .replace(/#{1,6}\s+/g, '') // Remove headings
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold
    .replace(/\*(.+?)\*/g, '$1') // Remove italic
    .replace(/`(.+?)`/g, '$1') // Remove inline code
    .replace(/\[(.+?)\]\(.+?\)/g, '$1') // Remove links, keep text
    .replace(/^[-*+]\s+/gm, '- ') // Normalize list markers
    .replace(/^\d+\.\s+/gm, '') // Remove numbered list markers
    .replace(/^>\s+/gm, '') // Remove blockquotes
    .replace(/---+/g, '') // Remove horizontal rules
    .replace(/\n{3,}/g, '\n\n') // Collapse multiple newlines
    .trim()
}

/**
 * Converts markdown to basic HTML for clipboard operations.
 */
export function markdownToHtml(text: string): string {
  return text
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>')
    .replace(/^[-*+]\s+(.+)$/gm, '<li>$1</li>')
    .replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>')
    .replace(/---+/g, '<hr>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>')
}

/**
 * Counts words in a text, ignoring markdown formatting.
 */
export function countWords(text: string): number {
  const plain = markdownToPlainText(text)
  return plain.split(/\s+/).filter(Boolean).length
}

/**
 * Converts heading text to a URL-safe slug (lowercase, no accents, hyphens).
 */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'section'
}

export type HeadingItem = { level: 1 | 2 | 3; text: string; id: string }

/**
 * Extracts h1/h2/h3 headings from markdown and returns them with slugs for anchor links.
 * Duplicate slugs get a numeric suffix (-1, -2, ...).
 */
export function extractHeadings(markdown: string): HeadingItem[] {
  const regex = /^(#{1,3})\s+(.+)$/gm
  const items: HeadingItem[] = []
  const slugCounts = new Map<string, number>()
  let m: RegExpExecArray | null
  while ((m = regex.exec(markdown)) !== null) {
    const level = m[1].length as 1 | 2 | 3
    const rawText = m[2].replace(/\s*\{#.*\}\s*$/, '').trim()
    const text = rawText.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1').trim()
    let baseSlug = slugify(text)
    const count = slugCounts.get(baseSlug) ?? 0
    slugCounts.set(baseSlug, count + 1)
    const id = count === 0 ? baseSlug : `${baseSlug}-${count}`
    items.push({ level, text, id })
  }
  return items
}

const HEADING_LINE_REGEX = /^(#{1,3})\s+(.+)$/

/**
 * Extracts the markdown section for the heading at the given index: from that heading line
 * up to (but not including) the next heading of the same or higher level, or end of document.
 */
export function extractSectionByIndex(markdown: string, headingIndex: number): string {
  if (!markdown || headingIndex < 0) return ''
  const lines = markdown.split('\n')
  const headingLineIndices: number[] = []
  for (let i = 0; i < lines.length; i++) {
    if (HEADING_LINE_REGEX.test(lines[i])) headingLineIndices.push(i)
  }
  if (headingIndex >= headingLineIndices.length) return ''
  const start = headingLineIndices[headingIndex]
  const end =
    headingIndex + 1 < headingLineIndices.length
      ? headingLineIndices[headingIndex + 1] - 1
      : lines.length - 1
  return lines.slice(start, end + 1).join('\n').trim()
}

/** Matches citation refs [1], [2], [12] in text (not markdown links [text](url)). */
const CITATION_REF_REGEX = /\[(\d+)\](?!\()/g

/**
 * Shape of structured content when body is stored as JSON (e.g. from writer output).
 */
export interface StructuredBodyFields {
  title?: string | null
  excerpt?: string | null
  body: string
  cta?: string | null
}

/**
 * If the given string is (or contains) a JSON object with title/excerpt/body/cta,
 * returns the parsed fields for display. Handles raw JSON and markdown code fences.
 * Otherwise returns null (caller should use piece fields as-is).
 */
export function parseStructuredBody(raw: string | null | undefined): StructuredBodyFields | null {
  if (!raw || typeof raw !== 'string') return null
  const trimmed = raw.trim()
  let toParse = trimmed
  const codeFence = /^```(?:json)?\s*\n?([\s\S]*?)\n?```\s*$/m
  const match = trimmed.match(codeFence)
  if (match) toParse = match[1].trim()
  if (!toParse.startsWith('{')) return null
  try {
    const obj = JSON.parse(toParse) as Record<string, unknown>
    if (obj && typeof obj === 'object' && 'body' in obj) {
      return {
        title: typeof obj.title === 'string' ? obj.title : null,
        excerpt: typeof obj.excerpt === 'string' ? obj.excerpt : null,
        body: typeof obj.body === 'string' ? obj.body : String(obj.body ?? ''),
        cta: typeof obj.cta === 'string' ? obj.cta : null,
      }
    }
  } catch {
    // not valid JSON
  }
  return null
}

export interface CopySource {
  id: number
  title: string
  url: string
  domain?: string
}

/**
 * Appends a formatted sources/references section to body text for clipboard operations.
 * Returns empty string when sources is empty or undefined.
 */
export function formatSourcesSection(
  sources: CopySource[] | undefined | null,
  format: 'markdown' | 'plain' | 'html',
): string {
  if (!sources || sources.length === 0) return ''

  switch (format) {
    case 'markdown': {
      const lines = sources.map((s, i) => `${i + 1}. [${s.title || s.domain || s.url}](${s.url})`)
      return `\n\n---\n\n## Fuentes\n\n${lines.join('\n')}`
    }
    case 'plain': {
      const lines = sources.map((s, i) => `${i + 1}. ${s.title || s.domain || s.url} - ${s.url}`)
      return `\n\n---\n\nFuentes:\n${lines.join('\n')}`
    }
    case 'html': {
      const items = sources
        .map((s) => `<li><a href="${s.url}">${s.title || s.domain || s.url}</a></li>`)
        .join('')
      return `<hr><h2>Fuentes</h2><ol>${items}</ol>`
    }
  }
}

/**
 * Returns sorted unique citation numbers found in the body (e.g. [1], [2], [10] -> [1, 2, 10]).
 * Used to render placeholder "Referencias" anchors when piece.sources is empty so [N] links work.
 */
export function extractCitationNumbers(body: string): number[] {
  if (!body) return []
  const set = new Set<number>()
  let m: RegExpExecArray | null
  CITATION_REF_REGEX.lastIndex = 0
  while ((m = CITATION_REF_REGEX.exec(body)) !== null) {
    set.add(parseInt(m[1], 10))
  }
  return Array.from(set).sort((a, b) => a - b)
}
